import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Mail, CheckCircle2, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface NewsletterSignupProps {
  source: string; // Where signup originated (e.g., 'insights_page', 'homepage')
  variant?: 'default' | 'compact' | 'inline';
  title?: string; // Custom title
  description?: string; // Custom description
  className?: string;
}

const NewsletterSignup = ({
  source,
  variant = 'default',
  title,
  description,
  className = ''
}: NewsletterSignupProps) => {
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setMessage('');

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setStatus('error');
      setMessage('Please enter a valid email address');
      return;
    }

    try {
      // Track newsletter signup attempt
      if (typeof window !== 'undefined' && (window as unknown).gtag) {
        (window as unknown).gtag('event', 'newsletter_signup_attempt', {
          event_category: 'engagement',
          source: source,
        });
      }

      const { data, error } = await supabase.functions.invoke('api-newsletter-signup', {
        body: {
          email: email.trim(),
          source: source,
          honeypot,
          metadata: {
            page_url: typeof window !== 'undefined' ? window.location.href : '',
            user_agent: typeof window !== 'undefined' ? navigator.userAgent : '',
            timestamp: new Date().toISOString()
          }
        }
      });

      if (error) {
        throw error;
      }

      if (data?.success) {
        setStatus('success');
        setMessage(data.message || 'Thank you for subscribing!');
        setEmail('');

        // Track successful signup
        if (typeof window !== 'undefined' && (window as unknown).gtag) {
          (window as unknown).gtag('event', 'newsletter_signup', {
            event_category: 'engagement',
            source: source,
            already_subscribed: data.already_subscribed || false,
            reactivated: data.reactivated || false
          });
        }
      } else {
        throw new Error(data?.error || 'Failed to subscribe');
      }
    } catch (error: unknown) {
      setStatus('error');
      setMessage(error.message || 'Something went wrong. Please try again.');
      
      // Track error
      if (typeof window !== 'undefined' && (window as unknown).gtag) {
        (window as unknown).gtag('event', 'newsletter_signup_error', {
          event_category: 'engagement',
          source: source,
          error: error.message
        });
      }
    }
  };

  const honeypotField = (
    <div className="absolute opacity-0 -z-10 h-0 overflow-hidden" aria-hidden="true" tabIndex={-1}>
      <label htmlFor={`nl-hp-${source}`}>Website</label>
      <input
        id={`nl-hp-${source}`}
        name="website"
        type="text"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
      />
    </div>
  );

  // Inline variant - just the form, no card
  if (variant === 'inline') {
    return (
      <form onSubmit={handleSubmit} className={`flex gap-2 relative ${className}`}>
        {honeypotField}
        <Input
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={status === 'loading'}
          className="flex-1"
        />
        <Button type="submit" disabled={status === 'loading'}>
          {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
        </Button>
      </form>
    );
  }

  // Compact variant - minimal card
  if (variant === 'compact') {
    return (
      <Card className={className}>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-3 relative">
            {honeypotField}
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={status === 'loading'}
                className="flex-1"
              />
              <Button type="submit" disabled={status === 'loading'}>
                <Mail className="mr-2 h-4 w-4" />
                {status === 'loading' ? '...' : 'Subscribe'}
              </Button>
            </div>
            {status === 'success' && (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle2 className="h-4 w-4" />
                <span>{message}</span>
              </div>
            )}
            {status === 'error' && (
              <div className="flex items-center gap-2 text-sm text-red-600">
                <AlertCircle className="h-4 w-4" />
                <span>{message}</span>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              We respect your privacy. Unsubscribe at any time.
            </p>
          </form>
        </CardContent>
      </Card>
    );
  }

  // Default variant - full card with title and description
  return (
    <Card className={`bg-primary/5 border-primary/20 ${className}`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-primary" />
          {title || 'Stay Updated with Career Insights'}
        </CardTitle>
        <CardDescription>
          {description || 'Get weekly career insights, salary trends, and job market analysis delivered to your inbox.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-3 relative">
          {honeypotField}
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={status === 'loading'}
              className="flex-1"
            />
            <Button type="submit" disabled={status === 'loading'}>
              <Mail className="mr-2 h-4 w-4" />
              {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
            </Button>
          </div>
          {status === 'success' && (
            <div className="flex items-center gap-2 text-sm text-green-600">
              <CheckCircle2 className="h-4 w-4" />
              <span>{message}</span>
            </div>
          )}
          {status === 'error' && (
            <div className="flex items-center gap-2 text-sm text-red-600">
              <AlertCircle className="h-4 w-4" />
              <span>{message}</span>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            We respect your privacy. Unsubscribe at any time.
          </p>
        </form>
      </CardContent>
    </Card>
  );
};

export default NewsletterSignup;

